import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { StudioCardWork } from './studioCardWork';
import { studioCardWorkMessages } from './studioCardWorkMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from './Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ThemeCardWorkChipsProps {
  work: StudioCardWork;
}

/**
 * The work chips of a step-2 card, in place of "Ready to make": "1 making" (a pulsing dot, the
 * colours of the Work page "working" badge), "2 ready" (green) and "1 failed" (red). A chip shows
 * only for a number above 0.
 */
const ThemeCardWorkChipsComponent: React.FC<ThemeCardWorkChipsProps> = ({ work }) => {
  const intl = useIntl();
  return (
    <>
      {work.making > 0 && (
        <span className={clsx(styles.chip, styles.chipMaking)} data-testid="studio-card-making">
          <i aria-hidden="true" />
          {intl.formatMessage(studioCardWorkMessages.making, { count: work.making })}
        </span>
      )}
      {work.ready > 0 && (
        <span className={clsx(styles.chip, styles.chipOk)} data-testid="studio-card-ready">
          {intl.formatMessage(studioCardWorkMessages.ready, { count: work.ready })}
        </span>
      )}
      {work.failed > 0 && (
        <span className={clsx(styles.chip, styles.chipBad)} data-testid="studio-card-failed">
          {intl.formatMessage(studioCardWorkMessages.failed, { count: work.failed })}
        </span>
      )}
    </>
  );
};

export const ThemeCardWorkChips = TypedMemo(ThemeCardWorkChipsComponent);

interface ThemeCardWorkBarProps {
  progress: number;
}

/**
 * The thin bar at the foot of a card while a run is being made: the render percent, or a moving
 * stripe while the run waits, plans or reads the song (the step bar of the Work page).
 */
const ThemeCardWorkBarComponent: React.FC<ThemeCardWorkBarProps> = ({ progress }) => {
  const intl = useIntl();
  const percent = Math.max(0, Math.min(100, Math.round(progress)));
  return (
    <span
      className={clsx(styles.cardBar, percent === 0 && styles.cardBarWait)}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-label={intl.formatMessage(studioCardWorkMessages.progress, { percent })}
      data-testid="studio-card-progress"
    >
      <i style={percent === 0 ? undefined : { width: `${percent}%` }} />
    </span>
  );
};

export const ThemeCardWorkBar = TypedMemo(ThemeCardWorkBarComponent);

interface ThemeCardWorkActionsProps {
  work: StudioCardWork;
  /** Opens the Work page of the song: the working runs, or the failed ones. */
  onOpenWork: (state: 'working' | 'failed') => void;
  /** Opens the result of the newest ready run (the Studio result, or the reel review). */
  onOpenReady: (work: StudioCardWork) => void;
}

/**
 * The line under a card with work (it joins the card like the s7c note): "Open" for a ready run,
 * "See the work" for a run that is being made or that failed (it opens the Work page).
 */
const ThemeCardWorkActionsComponent: React.FC<ThemeCardWorkActionsProps> = ({ work, onOpenWork, onOpenReady }) => {
  const intl = useIntl();
  const canOpen = work.readyAsset !== null || work.readyReel !== null;
  const workState = work.failed > 0 && work.making === 0 ? 'failed' : 'working';
  const showWork = work.making > 0 || work.failed > 0;
  return (
    <span className={styles.cardWorkActions}>
      {canOpen && (
        <button
          type="button"
          className="btn btn-link btn-sm p-0 align-baseline"
          onClick={() => onOpenReady(work)}
          data-testid="studio-card-open"
        >
          {intl.formatMessage(studioCardWorkMessages.open)}
        </button>
      )}
      {showWork && (
        <button
          type="button"
          className="btn btn-link btn-sm p-0 align-baseline"
          onClick={() => onOpenWork(workState)}
          data-testid="studio-card-see-work"
        >
          {intl.formatMessage(studioCardWorkMessages.seeWork)}
        </button>
      )}
    </span>
  );
};

export const ThemeCardWorkActions = TypedMemo(ThemeCardWorkActionsComponent);
